import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LocalLink as Link, stripLocale } from '@/lib/locale';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Menu, ShoppingBag, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/Logo';
import { categoryName, fetchCategories } from '@/lib/catalog';

const pill =
  'inline-flex min-h-[44px] items-center rounded-full px-4 text-[15px] font-medium text-candy-ink transition-colors duration-150 ease-out hover:bg-candy-surface';

export const Navbar = ({ cartCount, onCartOpen }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [bump, setBump] = useState(false);
  const prevCount = useRef(cartCount);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const path = stripLocale(location.pathname);

  useEffect(() => {
    fetchCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  // "Spring" the cart counter whenever something is added
  useEffect(() => {
    if (cartCount > prevCount.current) {
      setBump(true);
      const id = setTimeout(() => setBump(false), 450);
      prevCount.current = cartCount;
      return () => clearTimeout(id);
    }
    prevCount.current = cartCount;
  }, [cartCount]);

  useEffect(() => setMobileMenuOpen(false), [location.pathname, location.hash]);

  const onHome = path === '/';
  const links = [
    { to: { pathname: '/', hash: '#bears' }, label: t('nav.bears'), active: onHome && location.hash !== '#how' && location.hash !== '#faq', key: 'bears' },
    { to: '/shop', label: t('nav.shopAll'), active: path.startsWith('/shop') || path.startsWith('/category'), key: 'shop', testId: 'shop-link' },
    { to: { pathname: '/', hash: '#how' }, label: t('nav.how'), active: onHome && location.hash === '#how', key: 'how' },
    { to: { pathname: '/', hash: '#faq' }, label: t('nav.faq'), active: onHome && location.hash === '#faq', key: 'faq' },
  ];

  const counter = (
    <span
      data-testid="cart-count"
      className={`inline-block tabular-nums ${bump ? 'motion-safe:animate-spring' : ''}`}
    >
      {cartCount}
    </span>
  );

  return (
    <>
      <div className="bg-candy-ink px-4 py-2 text-center text-[13px] font-medium text-candy-bg md:py-2.5 md:text-sm">
        {t('nav.strip')}
      </div>

      <header
        data-testid="navbar"
        className="sticky top-0 z-40 bg-candy-bg/95 backdrop-blur md:static md:bg-transparent md:backdrop-blur-0"
      >
        <div className="mx-auto flex h-[var(--header-h)] max-w-content items-center justify-between gap-2 px-4 max-[359px]:px-3 md:h-auto md:flex-wrap md:gap-5 md:px-6 md:py-[18px]">
          <Logo />

          <nav aria-label="Main" className="hidden flex-wrap gap-1.5 md:flex">
            {links.map((l) => (
              <Link
                key={l.key}
                to={l.to}
                data-testid={l.testId}
                aria-current={l.active ? 'page' : undefined}
                className={`${pill} ${l.active ? 'bg-candy-surface font-bold' : ''}`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-2 md:flex">
            <LanguageSwitcher />
            <button
              type="button"
              data-testid="cart-button"
              onClick={onCartOpen}
              className="inline-flex h-[46px] items-center gap-2 rounded-full border-2 border-candy-ink bg-candy-surface px-5 text-[15px] font-bold text-candy-ink transition-colors duration-150 ease-out hover:bg-candy-pink"
            >
              <ShoppingBag className="h-[18px] w-[18px]" strokeWidth={2.2} aria-hidden="true" />
              <span>
                {t('nav.cart')} ({counter})
              </span>
            </button>
          </div>

          {/* Mobile: language flag + cart icon + burger */}
          <div className="flex items-center gap-1 md:hidden">
            <LanguageSwitcher />
            <button
              type="button"
              onClick={onCartOpen}
              aria-label={`${t('nav.cart')} (${cartCount})`}
              className="relative grid h-11 w-11 place-items-center rounded-full border-2 border-candy-ink bg-candy-surface text-candy-ink"
            >
              <ShoppingBag className="h-5 w-5" aria-hidden="true" />
              {cartCount > 0 && (
                <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-accent px-1 text-[11px] font-bold text-white">
                  {counter}
                </span>
              )}
            </button>
            <button
              type="button"
              data-testid="mobile-menu-button"
              onClick={() => setMobileMenuOpen((o) => !o)}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
              className="grid h-11 w-11 place-items-center rounded-full text-candy-ink"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" aria-hidden="true" /> : <Menu className="h-6 w-6" aria-hidden="true" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <nav
            id="mobile-menu"
            aria-label="Mobile"
            data-testid="mobile-menu"
            className="max-h-[calc(100vh-var(--header-h))] overflow-y-auto border-t border-candy-line bg-candy-bg px-4 pb-6 pt-3 md:hidden"
          >
            <div className="flex flex-col gap-1">
              {links.map((l) => (
                <Link
                  key={l.key}
                  to={l.to}
                  data-testid={l.testId ? `mobile-${l.testId}` : undefined}
                  className={`${pill} ${l.active ? 'bg-candy-surface font-bold' : ''}`}
                >
                  {l.label}
                </Link>
              ))}
            </div>

            {categories.length > 0 && (
              <div className="mt-4 border-t border-candy-line pt-4">
                <p className="mb-1 px-4 text-xs font-bold uppercase tracking-wider text-candy-ink-muted">{t('nav.categories')}</p>
                {categories.map((c) => (
                  <Link
                    key={c.category_id}
                    to={`/category/${c.slug}`}
                    data-testid={`mobile-cat-${c.slug}`}
                    className={`${pill} w-full justify-between`}
                  >
                    <span>{categoryName(c, i18n.language)}</span>
                    <span className="text-xs text-candy-ink-muted">{c.product_count}</span>
                  </Link>
                ))}
              </div>
            )}

          </nav>
        )}
      </header>
    </>
  );
};
